import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AdapterFormBase } from '../adapter-form-base.interface';
import { SentinelAdapterConfig } from 'src/app/services/httpServices/adaptersHttpService/adapters-http.service';
import { uuidValidator, CREDENTIAL_PLACEHOLDER } from '../validators/adapter-validators';
import { dceUriValidator, dcrIdValidator } from './sentinel-adapter-validators';
import { SENTINEL_SETUP_SCRIPT_FILENAME } from './sentinel-automated-setup/sentinel-automated-setup.component';
import { HttpService } from 'src/app/services/httpServices/http/http.service';
import { catchError, tap } from 'rxjs/operators';
import { EMPTY } from 'rxjs';

@Component({
  selector: 'app-sentinel-adapter-form',
  templateUrl: './sentinel-adapter-form.component.html',
  styleUrls: ['./sentinel-adapter-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SentinelAdapterFormComponent implements OnInit, AdapterFormBase {
  @Input() isEditMode = false;

  public adapterForm!: FormGroup;

  constructor(
    private readonly _fb: FormBuilder,
    private readonly _httpService: HttpService
  ) {}

  ngOnInit(): void {
    this._initializeForm();
  }

  private _initializeForm(): void {
    // In edit mode, credential is optional (only required if changing it)
    const clientSecretValidators = this.isEditMode ? [] : [Validators.required];

    this.adapterForm = this._fb.group({
      tenantId: ['', [Validators.required, uuidValidator('Tenant ID', this.isEditMode)]],
      clientId: ['', [Validators.required, uuidValidator('Client ID', this.isEditMode)]],
      clientSecret: ['', clientSecretValidators],
      dceUri: ['', [Validators.required, dceUriValidator()]],
      dcrImmutableId: ['', [Validators.required, dcrIdValidator()]],
      streamName: ['', [Validators.required]],
    });
  }

  public initializeForm(data: SentinelAdapterConfig): void {
    if (data) {
      this.adapterForm.patchValue({
        tenantId: data.tenantId,
        clientId: data.clientId,
        clientSecret: CREDENTIAL_PLACEHOLDER, // Show placeholder, don't expose actual secret
        dceUri: data.dceUri,
        dcrImmutableId: data.dcrImmutableId,
        streamName: data.streamName,
      });
    }
  }

  public getFormValue(): Partial<SentinelAdapterConfig> {
    const formValue = this.adapterForm.value;
    const config: Partial<SentinelAdapterConfig> = {
      adapterType: 'sentinel',
    };

    if (formValue.tenantId != null && formValue.tenantId.trim() !== '') {
      config.tenantId = formValue.tenantId;
    }
    if (formValue.clientId != null && formValue.clientId.trim() !== '') {
      config.clientId = formValue.clientId;
    }
    // Only include clientSecret if it's not the placeholder (user changed it)
    if (
      formValue.clientSecret != null &&
      formValue.clientSecret.trim() !== '' &&
      formValue.clientSecret !== CREDENTIAL_PLACEHOLDER
    ) {
      config.clientSecret = formValue.clientSecret;
    }
    if (formValue.dceUri != null && formValue.dceUri.trim() !== '') {
      config.dceUri = formValue.dceUri;
    }
    if (formValue.dcrImmutableId != null && formValue.dcrImmutableId.trim() !== '') {
      config.dcrImmutableId = formValue.dcrImmutableId;
    }
    if (formValue.streamName != null && formValue.streamName.trim() !== '') {
      config.streamName = formValue.streamName;
    }

    return config;
  }

  public isValid(): boolean {
    return this.adapterForm.valid;
  }

  /**
   * Downloads the Sentinel setup script from the backend.
   * Uses HttpClient with streaming to properly handle authentication and download.
   */
  public downloadScript(event?: Event): void {
    if (event) {
      event.preventDefault();
    }

    // Use the generic adapter setup script endpoint with sentinel type
    this._httpService
      .getStream('adapters/sentinel/setup-script')
      .pipe(
        tap((blob: Blob) => {
          // Create a temporary URL for the blob
          const blobUrl = window.URL.createObjectURL(blob);

          // Create and trigger download
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = SENTINEL_SETUP_SCRIPT_FILENAME;
          document.body.appendChild(link);
          link.click();

          // Cleanup
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        }),
        catchError((error) => {
          console.error('Error downloading Sentinel setup script:', error);
          // TODO: Add toast notification for error
          return EMPTY;
        })
      )
      .subscribe();
  }
}
